import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const threadsWriteSuccessSchema = z.object({
  ok: z.literal(true),
});

async function parseThreadsWriteSuccess(
  response: Response,
  fallbackMessage: string,
): Promise<z.infer<typeof threadsWriteSuccessSchema>> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = threadsWriteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(fallbackMessage, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export function followThreadPath(threadId: string): string {
  return `/api/threads/${encodeURIComponent(threadId)}/follow`;
}

export function unfollowThreadPath(threadId: string): string {
  return `/api/threads/${encodeURIComponent(threadId)}/unfollow`;
}

export async function followThreadRequest({
  apiBaseUrl,
  accessToken,
  threadId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  threadId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof threadsWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${followThreadPath(threadId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to follow thread");
  }

  return parseThreadsWriteSuccess(response, "Invalid follow thread response");
}

export async function unfollowThreadRequest({
  apiBaseUrl,
  accessToken,
  threadId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  threadId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof threadsWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${unfollowThreadPath(threadId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to unfollow thread");
  }

  return parseThreadsWriteSuccess(response, "Invalid unfollow thread response");
}
